import { useEffect, useState } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { getSectorBySlug, BusinessSector } from '@/services/content'
import { ScrollReveal } from '@/components/ui/scroll-reveal'
import { Button } from '@/components/ui/button'
import { SectorLeadForm } from '@/components/landing/SectorLeadForm'
import { SectorTestimonialsSection } from '@/components/landing/SectorTestimonialsSection'
import { SectorFaqSection } from '@/components/landing/SectorFaqSection'
import { SectorPracticalExamplesSection } from '@/components/landing/SectorPracticalExamplesSection'
import {
  AlertTriangle,
  ArrowRight,
  BrainCircuit,
  CheckCircle2,
  TrendingUp,
  ShieldAlert,
  Loader2,
  Sparkles,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { iconMap } from '@/lib/icons'

const SectorPage = () => {
  const { slug } = useParams<{ slug: string }>()
  const [sector, setSector] = useState<BusinessSector | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const fetchSector = async () => {
      if (!slug) return
      setLoading(true)
      const { data, error } = await getSectorBySlug(slug)
      if (error || !data) {
        setError(true)
      } else {
        setSector(data)
        // Update Meta Tags
        if (data.page_content?.seo) {
          document.title =
            data.page_content.seo.title || `${data.title} - Núcleo Digital`
          const metaDesc = document.querySelector('meta[name="description"]')
          if (metaDesc) {
            metaDesc.setAttribute(
              'content',
              data.page_content.seo.description || '',
            )
          }
        }
      }
      setLoading(false)
    }

    fetchSector()
  }, [slug])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-white min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !sector || !sector.page_content) {
    return <Navigate to="/404" replace />
  }

  const {
    hero,
    problems,
    saas_examples,
    practical_examples,
    ai_capabilities,
    business_impact,
    form_config,
  } = sector.page_content

  const scrollToForm = () => {
    document
      .getElementById('sector-form')
      ?.scrollIntoView({ behavior: 'smooth' })
  }

  const SectorIcon = iconMap[sector.icon_name] || iconMap['Globe']

  return (
    <div className="flex flex-col w-full">
      {/* Block 1: Hero */}
      <section className="relative py-24 md:py-32 bg-slate-50 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-indigo-50/50 rounded-bl-[100px] -z-10" />
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <ScrollReveal>
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-white rounded-2xl shadow-sm ring-1 ring-slate-100">
                  <SectorIcon className="w-10 h-10 text-primary" />
                </div>
              </div>
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-medium text-sm mb-4">
                {sector.title}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
                {hero?.title}
              </h1>
              <div className="pt-6">
                <Button
                  size="lg"
                  className="h-14 px-8 text-lg shadow-lg hover:translate-y-[-2px] transition-all"
                  onClick={scrollToForm}
                >
                  {hero?.cta}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Block 2: Typical Problems */}
      <section className="py-24 bg-white">
        <div className="container px-4 md:px-6">
          <ScrollReveal className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 text-orange-600 mb-6">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900">
              Desafios Típicos do Setor
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Identificamos os problemas que mais limitam o crescimento neste
              setor.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {problems?.map((problem, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="flex items-start gap-4 p-6 rounded-xl border border-slate-100 hover:border-orange-200 hover:bg-orange-50/30 transition-all">
                  <div className="mt-1 min-w-[20px]">
                    <ShieldAlert className="w-5 h-5 text-orange-500" />
                  </div>
                  <span className="text-slate-700 font-medium">{problem}</span>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Block 3: SaaS Examples */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="container px-4 md:px-6">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">
              Soluções Implementadas
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Exemplos reais de como transformamos operações.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {saas_examples?.map((example, idx) => (
              <ScrollReveal key={idx} delay={idx * 150}>
                <Card className="h-full bg-white hover:shadow-xl transition-all hover:-translate-y-1 duration-300 border-t-4 border-t-primary/80">
                  <CardContent className="p-8 space-y-4">
                    <h3 className="text-xl font-bold text-slate-900">
                      {example.name}
                    </h3>
                    <p className="text-slate-600 min-h-[48px]">
                      {example.description}
                    </p>
                    <div className="pt-4 mt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                        <TrendingUp className="w-4 h-4" />
                        {example.benefit}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Block 4: Practical Examples (New) */}
      {practical_examples && practical_examples.length > 0 && (
        <SectorPracticalExamplesSection examples={practical_examples} />
      )}

      {/* Block 6: AI Potential (Standard) */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 rounded-full blur-[100px]" />
        </div>

        <div className="container px-4 md:px-6 relative z-10">
          <ScrollReveal className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 text-blue-300 mb-6 backdrop-blur-sm">
              <BrainCircuit className="w-6 h-6" />
            </div>
            <h2 className="text-3xl font-bold text-white">
              Potencial da Inteligência Artificial
            </h2>
            <p className="text-slate-300 mt-4 text-lg max-w-2xl mx-auto">
              Como a IA pode trabalhar silenciosamente para otimizar o seu
              negócio.
            </p>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            {ai_capabilities?.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="flex items-center gap-4 p-5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors backdrop-blur-sm">
                  <CheckCircle2 className="w-6 h-6 text-blue-400 flex-shrink-0" />
                  <span className="text-slate-200 font-medium">{item}</span>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* AI Summary Highlight */}
          {sector.ai_summary && (
            <ScrollReveal delay={400} className="max-w-4xl mx-auto">
              <div className="bg-indigo-900/40 border border-indigo-500/30 rounded-2xl p-8 md:p-10 backdrop-blur-md relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl -mr-10 -mt-10" />
                <div className="flex flex-col md:flex-row gap-8 items-start relative z-10">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <Sparkles className="text-indigo-400 w-6 h-6" />
                      <h3 className="text-xl font-bold text-white">
                        {sector.ai_summary.title}
                      </h3>
                    </div>
                    <p className="text-slate-300 leading-relaxed mb-6">
                      {sector.ai_summary.content}
                    </p>
                  </div>
                  <div className="w-full md:w-1/3 bg-white/5 rounded-xl p-6 border border-white/10">
                    <h4 className="text-sm font-semibold text-indigo-300 uppercase tracking-wider mb-4">
                      Destaques
                    </h4>
                    <ul className="space-y-3">
                      {sector.ai_summary.highlights?.map((highlight, idx) => (
                        <li key={idx} className="flex gap-2 text-sm text-white">
                          <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          )}
        </div>
      </section>

      {/* Block 7: Business Impact */}
      <section className="py-24 bg-white">
        <div className="container px-4 md:px-6">
          <ScrollReveal className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">
              Impacto no Negócio
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Resultados típicos observados após a digitalização.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {business_impact?.map((item, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="flex flex-col items-center justify-center text-center p-6 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-lg transition-all border border-slate-100 group">
                  <span className="text-3xl md:text-4xl font-bold text-primary mb-2 group-hover:scale-110 transition-transform">
                    {item.value}
                  </span>
                  <span className="text-sm md:text-base text-slate-600 font-medium">
                    {item.label}
                  </span>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Block 8: Testimonials */}
      <SectorTestimonialsSection
        sectorId={sector.id}
        sectorTitle={sector.title}
      />

      {/* Block 9: FAQ */}
      {sector.faq_structured_data && (
        <SectorFaqSection faqs={sector.faq_structured_data} />
      )}

      {/* Block 10: Lead Form */}
      <section id="sector-form" className="py-24 bg-slate-50">
        <div className="container px-4 md:px-6">
          <ScrollReveal>
            <SectorLeadForm sectorTitle={sector.title} config={form_config} />
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}

export default SectorPage
